import { Suspense, useEffect, useState } from 'react';
import '@blueprintjs/icons/lib/css/blueprint-icons.css';
import '@blueprintjs/core/lib/css/blueprint.css';
import './assets/styles/sass/main.scss';
import { Auth } from 'aws-amplify';
import { ToastContainer } from 'react-toastify';
import OverwhelmedModal from './components/OverwhelmedModal';
import { displayErrorToast } from './utils/ToastUtils';
import { useAppDispatch, useAppSelector } from './store/store';
import { getApiStatus } from './store/selector/user.selector';
import { refreshToken } from './utils/auth.utils';
import { useLazyGetUserDetailsQuery } from './store/api/user.api';
import { SetApiStatus, SetUserDetails, SetUserDetailsExceptId } from './store/slice/user.slice';
import { Paddle, initializePaddle } from '@paddle/paddle-js';
import RoutesContainer from './Routes/RoutesContainer';
import {
    getOrganizationEmail,
    getOrganizationId,
    getOrganizationSubscriptionStatus,
} from './store/selector/organization.selector';
import {
    SetOrganizationDetails,
    SetOrganizationSubscriptionStatus,
} from './store/slice/organization.slice';
import { IGetUserDetailsResponse } from './interfaces/user.interface';
import { setUserAndAuthInitialState } from './utils/state.utils';

function App() {
    const dispatch = useAppDispatch();
    const organizationId = useAppSelector(getOrganizationId);
    const organizationEmail = useAppSelector(getOrganizationEmail);
    const organizationSubscriptionStatus = useAppSelector(getOrganizationSubscriptionStatus);
    const apiStatus = useAppSelector(getApiStatus);
    const [getUserDetails] = useLazyGetUserDetailsQuery();

    const [loadingApp, setLoadingApp] = useState(true);
    const [paddle, setPaddle] = useState<Paddle | null>(null);

    useEffect(() => {
        const checkUser = async () => {
            setLoadingApp(true);
            let tempUserDetails;

            try {
                await Auth.currentSession();
                tempUserDetails = await Auth.currentAuthenticatedUser();
            } catch (e) {
                await Auth.signOut();
                setUserAndAuthInitialState(dispatch);

                setLoadingApp(false);
                return;
            }

            try {
                if (tempUserDetails) {
                    await refreshToken(dispatch);
                    const response = await getUserDetails();

                    if (
                        response.error &&
                        'status' in response.error &&
                        response.error.status === 401
                    ) {
                        await Auth.signOut();
                        setUserAndAuthInitialState(dispatch);
                    } else {
                        const userData: IGetUserDetailsResponse = response.data;
                        dispatch(
                            SetUserDetails({
                                id: userData.id,
                                name: userData.name,
                                email: userData.email,
                                stage: userData.stage,
                                currentlyWorkingBrandId: userData.currentlyWorkingBrandId,
                                apiStatus: userData.apiStatus,
                            }),
                        );
                        dispatch(
                            SetOrganizationDetails({
                                id: userData.organization.id,
                                name: userData.organization.name,
                                email: userData.organization.email,
                                subscriptionStatus: userData.organization.subscriptionStatus,
                            }),
                        );
                    }
                    setLoadingApp(false);
                } else {
                    setUserAndAuthInitialState(dispatch);
                    setLoadingApp(false);
                }
            } catch (error) {
                displayErrorToast('Something happened. Please Refresh Page');
                setLoadingApp(false);
            }
        };

        checkUser();
    }, []);

    useEffect(() => {
        const intervalId = setInterval(async () => {
            await refreshToken(dispatch);
        }, 1800000);

        return () => clearInterval(intervalId);
    }, [dispatch]);

    useEffect(() => {
        const sellerID = parseInt(process.env.REACT_APP_PADDLE_SELLER_ID as string);
        if (sellerID && organizationId && organizationEmail)
            initializePaddle({
                environment: 'production',
                seller: sellerID,
                pwCustomer: organizationSubscriptionStatus ? { email: organizationEmail } : {},
                checkout: {
                    settings: {
                        displayMode: 'inline',
                        theme: 'light',
                        locale: 'en',
                        frameTarget: 'checkout-container',
                        frameInitialHeight: 450,
                        frameStyle:
                            'width: 100%; min-width: 312px; background-color: transparent; border: none;',
                    },
                },
                eventCallback: async function (data) {
                    if (data.name == 'checkout.completed') {
                        dispatch(SetOrganizationSubscriptionStatus(true));
                    }
                },
            }).then((paddleInstance: Paddle | undefined) => {
                if (paddleInstance) {
                    setPaddle(paddleInstance);
                }
            });
    }, [organizationId]);

    return (
        <>
            <OverwhelmedModal
                show={apiStatus === false}
                onHide={() => dispatch(SetApiStatus(true))}
            />
            <ToastContainer autoClose={3000} hideProgressBar />
            <Suspense fallback={<>Loading...</>}>
                {loadingApp ? <>Loading...</> : <RoutesContainer paddle={paddle} />}
            </Suspense>
        </>
    );
}

export default App;
